import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ChopProfil } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { FormButtonBarComponent } from '@shared/form-button-bar/form-button-bar.component';
import { FormTextInputComponent } from '@shared/form-text-input/form-text-input.component';
import { applyChopServerValidationError } from '../../../../chop-server-errors';

const FIELD_MAP = { Nummer: 'nummer', 'Bezeichnung.De': 'bezeichnung.De' } as const;

@Component({
  selector: 'app-chop-profil-dialog',
  templateUrl: './chop-profil-dialog.component.html',
  styleUrls: ['./chop-profil-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, FormTextInputComponent, FormButtonBarComponent],
})
export class ChopProfilDialogComponent {
  private formBuilder = inject(FormBuilder);
  private dialogConfig = inject(DynamicDialogConfig);
  private dialogRef = inject(DynamicDialogRef);
  private chopService = inject(ChopService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  private leistungsgruppeId: number = this.dialogConfig.data.leistungsgruppeId;
  private profil: ChopProfil | undefined = this.dialogConfig.data.profil;
  form = this.formBuilder.nonNullable.group({
    nummer: [this.profil?.nummer ?? '', Validators.required],
    bezeichnung: this.formBuilder.nonNullable.group({
      De: [this.profil?.bezeichnung?.De ?? '', Validators.required],
      Fr: [this.profil?.bezeichnung?.Fr ?? ''],
      It: [this.profil?.bezeichnung?.It ?? ''],
    }),
  });
  submitting = false;

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const value = this.form.getRawValue() as ChopProfil;
    const action = this.profil
      ? this.chopService.updateProfil(this.profil.id, { ...this.profil, ...value })
      : this.chopService.createProfil(this.leistungsgruppeId, value);
    action.pipe(finalize(() => (this.submitting = false))).subscribe({
      next: (result) => this.dialogRef.close(result),
      error: (err) => {
        if (!applyChopServerValidationError(this.form, err?.error, FIELD_MAP)) {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('GLOBAL.NOT_SUCCESSFULLY_SAVED'),
            detail: err?.error?.message,
          });
        }
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
